/**
 * ModalShell — the edit panel (moved from apps/admin-panel 0.53.0; same props).
 *
 * Desktop (900px and wider): a panel on the right-hand side, the list stays visible beside it.
 * Phone: a full-screen sheet. Both: title bar with a 44px close button, scrolling body, sticky
 * actions footer (Cancel / Save), Escape closes, Tab stays inside, focus returns on close, and the
 * page behind does not scroll. `width` widens the desktop panel (big editors ask for 620-720px).
 */

import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { useFocusTrap } from './focus';
import { ShellIcon } from './icons';

export interface ModalShellProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  /** Actions row at the bottom (Cancel + Save, typically). Stays in view while the body scrolls. */
  actions?: ReactNode;
  /** Desktop panel width (px number or CSS length). Default 480px. */
  width?: number | string;
  children: ReactNode;
}

let openCount = 0;

export function ModalShell({ open, title, onClose, actions, width, children }: ModalShellProps): JSX.Element | null {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(panel, open, onClose);

  // The page behind does not scroll while a panel is open (counted, panels can stack).
  useEffect(() => {
    if (!open) return undefined;
    openCount += 1;
    document.documentElement.classList.add('ps-modal-open');
    return () => {
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) document.documentElement.classList.remove('ps-modal-open');
    };
  }, [open]);

  if (!open) return null;
  const w = width === undefined ? undefined : typeof width === 'number' ? `${width}px` : width;
  return (
    <div className="ps-modal-backdrop" onClick={onClose}>
      <div
        ref={panel}
        className="ps-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={w ? ({ ['--ps-modal-w' as string]: w } as CSSProperties) : undefined}
      >
        <header className="ps-modal__header">
          <h2 className="ps-modal__title" id={titleId}>
            {title}
          </h2>
          <button type="button" className="ps-modal__close" aria-label="Close" onClick={onClose}>
            <ShellIcon name="close" size={20} />
          </button>
        </header>
        <div className="ps-modal__body">{children}</div>
        {actions && <footer className="ps-modal__actions">{actions}</footer>}
      </div>
    </div>
  );
}
